/**
 * Sysadmin Catalog → Argus Live detail (AL-C4).
 *
 * 4 tabs (mirroring catalog.tools.$id sysadmin pattern):
 *   - Overview  metadata + lifecycle action bar
 *   - Versions  list of published snapshots
 *   - Assignments  tenants that have cloned this template
 *   - Audit  events emitted for this template (read-only view)
 *
 * Lifecycle action bar:
 *   draft     → [Edit] [Delete] [Publish]
 *   published → [Edit] [Unpublish] [Archive]
 *   archived  → [Unarchive] [Delete]
 */

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import {
  ArrowLeft,
  LayoutDashboard,
  Edit,
  Trash2,
  Rocket,
  Archive,
  ArchiveRestore,
  PauseCircle,
  AlertTriangle,
  Loader2,
  Users as UsersIcon,
  Code as CodeIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  useSysadminSystemDashboard,
  useSysadminSystemDashboardVersions,
  useSysadminSystemDashboardAssignments,
  useUpdateSysadminSystemDashboard,
  useDeleteSysadminSystemDashboard,
  usePublishSysadminSystemDashboard,
  useUnpublishSysadminSystemDashboard,
  useArchiveSysadminSystemDashboard,
  useUnarchiveSysadminSystemDashboard,
  type SystemDashboardDetail,
  type ClassificationTag,
} from '@/lib/use-sysadmin-argus-live';

export const Route = createFileRoute('/sysadmin/catalog/argus-live/$id')({
  component: SysadminArgusLiveDetailPage,
});

interface EditDraft {
  title: string;
  description: string;
  icon: string;
  classificationCeiling: ClassificationTag | null;
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    draft: 'bg-slate-500/10 text-slate-700 dark:text-slate-300',
    published: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
    archived: 'bg-rose-500/10 text-rose-700 dark:text-rose-400',
  };
  return (
    <Badge className={`text-[10px] gap-1 hover:opacity-90 ${map[status]}`}>
      {status}
    </Badge>
  );
}

function SysadminArgusLiveDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const detailQuery = useSysadminSystemDashboard(id);
  const versionsQuery = useSysadminSystemDashboardVersions(id);
  const assignmentsQuery = useSysadminSystemDashboardAssignments(id);

  const update = useUpdateSysadminSystemDashboard();
  const del = useDeleteSysadminSystemDashboard();
  const publish = usePublishSysadminSystemDashboard();
  const unpublish = useUnpublishSysadminSystemDashboard();
  const archive = useArchiveSysadminSystemDashboard();
  const unarchive = useUnarchiveSysadminSystemDashboard();

  const [edit, setEdit] = useState<EditDraft | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);

  if (detailQuery.isLoading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (detailQuery.error) {
    return (
      <Card className="m-6 border-destructive/30">
        <CardContent className="py-6 text-sm text-destructive flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>Failed to load: {detailQuery.error.message}</div>
        </CardContent>
      </Card>
    );
  }
  if (!detailQuery.data?.data) {
    return null;
  }

  const { dashboard, currentVersion } = detailQuery.data
    .data as SystemDashboardDetail;
  const isDraft = dashboard.status === 'draft';
  const isPublished = dashboard.status === 'published';
  const isArchived = dashboard.status === 'archived';

  const onPublish = async () => {
    // Sysadmin publish path requires content. If there's no
    // currentVersion yet we open the dev tools flow (TODO: full
    // editor); for v1 we just refuse with a helpful message that
    // points to the future editor.
    if (!currentVersion) {
      toast.error('Cannot publish without content', {
        description:
          'Use the Edit Content button (coming in AL-C4 phase 2) to author HTML / JS / CSS, then publish.',
      });
      return;
    }
    try {
      await publish.mutateAsync({
        id: dashboard.id,
        existingVersionId: currentVersion.id,
      });
      toast.success('Published');
    } catch (err) {
      toast.error('Publish failed', {
        description: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const onUnpublish = async () => {
    try {
      await unpublish.mutateAsync({ id: dashboard.id });
      toast.success('Unpublished');
    } catch (err) {
      toast.error('Unpublish failed', {
        description: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const onArchiveConfirm = async () => {
    try {
      await archive.mutateAsync({ id: dashboard.id });
      setConfirmArchive(false);
      toast.success('Archived');
    } catch (err) {
      toast.error('Archive failed', {
        description: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const onUnarchive = async () => {
    try {
      await unarchive.mutateAsync({ id: dashboard.id });
      toast.success('Restored');
    } catch (err) {
      toast.error('Unarchive failed', {
        description: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const onDeleteConfirm = async () => {
    try {
      await del.mutateAsync({ id: dashboard.id });
      setConfirmDelete(false);
      toast.success('Deleted');
      navigate({ to: '/sysadmin/catalog/argus-live' });
    } catch (err) {
      toast.error('Delete failed', {
        description: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const onEditSave = async () => {
    if (!edit) return;
    try {
      await update.mutateAsync({
        id: dashboard.id,
        title: edit.title,
        description: edit.description || null,
        icon: edit.icon || null,
        classificationCeiling: edit.classificationCeiling,
      });
      setEdit(null);
      toast.success('Updated');
    } catch (err) {
      toast.error('Update failed', {
        description: err instanceof Error ? err.message : String(err),
      });
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => navigate({ to: '/sysadmin/catalog/argus-live' })}
          aria-label="Back"
          title="Back"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <LayoutDashboard className="h-6 w-6 text-muted-foreground" />
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-semibold truncate" title={dashboard.title}>
            {dashboard.title}
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="outline" className="text-[10px] font-mono">
              {dashboard.slug}
            </Badge>
            <StatusBadge status={dashboard.status} />
            {dashboard.classificationCeiling && (
              <Badge variant="outline" className="text-[9px]">
                ceiling: {dashboard.classificationCeiling.sensitivity}/
                {dashboard.classificationCeiling.exportControl}
              </Badge>
            )}
          </div>
        </div>

        {/* Lifecycle bar */}
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              setEdit({
                title: dashboard.title,
                description: dashboard.description ?? '',
                icon: dashboard.icon ?? '',
                classificationCeiling: dashboard.classificationCeiling,
              })
            }
            data-testid="edit-template-button"
          >
            <Edit className="h-3.5 w-3.5 mr-1.5" /> Edit
          </Button>

          {isDraft && (
            <Button
              size="sm"
              onClick={onPublish}
              disabled={publish.isPending}
              data-testid="publish-template-button"
            >
              {publish.isPending ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <Rocket className="h-3.5 w-3.5 mr-1.5" />
              )}
              Publish
            </Button>
          )}
          {isPublished && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={onUnpublish}
                disabled={unpublish.isPending}
                data-testid="unpublish-template-button"
              >
                <PauseCircle className="h-3.5 w-3.5 mr-1.5" /> Unpublish
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setConfirmArchive(true)}
                data-testid="archive-template-button"
              >
                <Archive className="h-3.5 w-3.5 mr-1.5" /> Archive
              </Button>
            </>
          )}
          {isArchived && (
            <Button
              size="sm"
              variant="outline"
              onClick={onUnarchive}
              disabled={unarchive.isPending}
              data-testid="unarchive-template-button"
            >
              <ArchiveRestore className="h-3.5 w-3.5 mr-1.5" /> Restore
            </Button>
          )}

          {(isDraft || isArchived) && (
            <Button
              size="sm"
              variant="destructive"
              onClick={() => setConfirmDelete(true)}
              data-testid="delete-template-button"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="versions">
            Versions
            <Badge variant="secondary" className="ml-1.5 text-[9px]">
              {versionsQuery.data?.items.length ?? 0}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="assignments">
            Assignments
            <Badge variant="secondary" className="ml-1.5 text-[9px]">
              {assignmentsQuery.data?.items.length ?? dashboard.cloneCount}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="audit">Audit</TabsTrigger>
        </TabsList>

        {/* Overview */}
        <TabsContent value="overview">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Identity</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <Row label="Slug">
                  <code className="font-mono text-xs">{dashboard.slug}</code>
                </Row>
                <Row label="Title">{dashboard.title}</Row>
                <Row label="Icon">{dashboard.icon ?? '—'}</Row>
                <Row label="Status">
                  <StatusBadge status={dashboard.status} />
                </Row>
                <Row label="Schema version">
                  v{dashboard.schemaVersion}
                </Row>
                <Row label="Description">
                  <p className="text-xs text-muted-foreground">
                    {dashboard.description ?? '—'}
                  </p>
                </Row>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Classification</CardTitle>
                <CardDescription>
                  Ceiling prevents tenants below this level from cloning.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <Row label="Sensitivity ceiling">
                  <Badge variant="outline" className="text-[10px]">
                    {dashboard.classificationCeiling?.sensitivity ??
                      'no ceiling'}
                  </Badge>
                </Row>
                <Row label="Export control ceiling">
                  <Badge variant="outline" className="text-[10px]">
                    {dashboard.classificationCeiling?.exportControl ??
                      'no ceiling'}
                  </Badge>
                </Row>
                <Row label="Current published rev">
                  {dashboard.currentVersionId ? (
                    <code className="text-[10px] font-mono">
                      {dashboard.currentVersionId.slice(0, 8)}…
                    </code>
                  ) : (
                    '—'
                  )}
                </Row>
                <Row label="Clone count">
                  <span className="inline-flex items-center gap-1">
                    <UsersIcon className="h-3 w-3" />
                    {dashboard.cloneCount}
                  </span>
                </Row>
              </CardContent>
            </Card>
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="text-sm">Timeline</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-3 text-xs">
                <Row label="Created">
                  {new Date(dashboard.createdAt).toLocaleString()}
                </Row>
                <Row label="Updated">
                  {new Date(dashboard.updatedAt).toLocaleString()}
                </Row>
                <Row label="Published">
                  {dashboard.publishedAt
                    ? new Date(dashboard.publishedAt).toLocaleString()
                    : '—'}
                </Row>
                <Row label="Archived">
                  {dashboard.archivedAt
                    ? new Date(dashboard.archivedAt).toLocaleString()
                    : '—'}
                </Row>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Versions */}
        <TabsContent value="versions">
          <Card>
            <CardContent className="px-0">
              {versionsQuery.isLoading ? (
                <div className="py-6 text-center text-muted-foreground">
                  <Loader2 className="h-4 w-4 inline animate-spin" />
                </div>
              ) : (versionsQuery.data?.items ?? []).length === 0 ? (
                <div className="py-10 text-center">
                  <CodeIcon className="h-8 w-8 mx-auto text-muted-foreground/40 mb-2" />
                  <p className="text-sm text-muted-foreground">
                    No versions yet
                  </p>
                  <p className="text-xs text-muted-foreground/70 mt-1">
                    Versions are created when you publish content.
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16">Seq</TableHead>
                      <TableHead className="w-32">Rev label</TableHead>
                      <TableHead className="w-44">Created</TableHead>
                      <TableHead className="w-32">Hash</TableHead>
                      <TableHead className="w-20">Bindings</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(versionsQuery.data?.items ?? []).map((v) => (
                      <TableRow
                        key={v.id}
                        data-testid={`version-row-${v.revSequence}`}
                      >
                        <TableCell className="text-xs tabular-nums">
                          {v.revSequence}
                        </TableCell>
                        <TableCell className="text-xs font-mono">
                          {v.revLabel}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground tabular-nums">
                          {new Date(v.createdAt).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-[10px] font-mono">
                          {v.contentHash.slice(0, 12)}…
                        </TableCell>
                        <TableCell className="text-xs tabular-nums">
                          {v.dataBindings.length}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Assignments */}
        <TabsContent value="assignments">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Tenant clones</CardTitle>
              <CardDescription>
                Tenants that have cloned this system template. Cloning is
                tenant-self-service; this list is a read-only view.
              </CardDescription>
            </CardHeader>
            <CardContent className="px-0">
              {assignmentsQuery.isLoading ? (
                <div className="py-6 text-center">
                  <Loader2 className="h-4 w-4 inline animate-spin" />
                </div>
              ) : (assignmentsQuery.data?.items ?? []).length === 0 ? (
                <div className="py-10 text-center text-sm text-muted-foreground">
                  No tenants have cloned this template yet.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tenant</TableHead>
                      <TableHead className="w-44">Cloned</TableHead>
                      <TableHead className="w-32">Tenant Dashboard</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(assignmentsQuery.data?.items ?? []).map((a) => (
                      <TableRow
                        key={a.organizationId}
                        data-testid={`assignment-row-${a.organizationId}`}
                      >
                        <TableCell className="text-sm">
                          {a.organizationName}
                          <div className="text-[10px] font-mono text-muted-foreground">
                            {a.organizationId.slice(0, 8)}…
                          </div>
                        </TableCell>
                        <TableCell className="text-xs tabular-nums">
                          {a.clonedAt
                            ? new Date(a.clonedAt).toLocaleString()
                            : '—'}
                        </TableCell>
                        <TableCell className="text-[10px] font-mono">
                          {a.tenantDashboardId
                            ? `${a.tenantDashboardId.slice(0, 8)}…`
                            : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit */}
        <TabsContent value="audit">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Audit trail</CardTitle>
              <CardDescription>
                Events emitted for this template land in the platform audit
                chain under the <code>argus_live.system.dashboard.*</code>{' '}
                action family. Use the sysadmin Audit rail for full
                cross-template search.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="p-4 rounded-md border bg-muted/30 text-xs text-muted-foreground">
                <p>Event types emitted by this template:</p>
                <ul className="list-disc list-inside mt-2 space-y-0.5 font-mono">
                  <li>argus_live.system.dashboard.created</li>
                  <li>argus_live.system.dashboard.updated</li>
                  <li>argus_live.system.dashboard.published</li>
                  <li>argus_live.system.dashboard.unpublished</li>
                  <li>argus_live.system.dashboard.archived</li>
                  <li>argus_live.system.dashboard.deleted</li>
                  <li>argus_live.system.dashboard.assigned</li>
                  <li>argus_live.dashboard.cloned_from_system (tenant scope)</li>
                </ul>
                <p className="mt-3">
                  Click{' '}
                  <Link
                    to="/sysadmin/audit"
                    className="underline hover:text-foreground"
                  >
                    Audit
                  </Link>{' '}
                  to drill into individual events.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit drawer */}
      <Sheet
        open={edit !== null}
        onOpenChange={(open) => !open && setEdit(null)}
      >
        <SheetContent side="right" className="sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Edit template</SheetTitle>
            <SheetDescription>
              Metadata only. Content (HTML / JS / CSS) is authored via the
              full editor (coming in AL-C4 phase 2).
            </SheetDescription>
          </SheetHeader>
          {edit && (
            <div className="space-y-4 mt-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Title</Label>
                <Input
                  value={edit.title}
                  onChange={(e) => setEdit({ ...edit, title: e.target.value })}
                  className="text-sm h-8"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Description</Label>
                <Textarea
                  value={edit.description}
                  onChange={(e) =>
                    setEdit({ ...edit, description: e.target.value })
                  }
                  rows={3}
                  className="text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Icon</Label>
                <Input
                  value={edit.icon}
                  onChange={(e) => setEdit({ ...edit, icon: e.target.value })}
                  placeholder="LayoutDashboard"
                  className="text-sm h-8"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEdit(null)}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={onEditSave}
                  disabled={!edit.title || update.isPending}
                >
                  {update.isPending ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Confirm dialogs */}
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete template?</AlertDialogTitle>
            <AlertDialogDescription>
              Soft-delete the system template <code>{dashboard.slug}</code>.
              Existing tenant clones are unaffected. Cannot be undone via the
              UI (the row is preserved for audit retention).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onDeleteConfirm}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmArchive} onOpenChange={setConfirmArchive}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive template?</AlertDialogTitle>
            <AlertDialogDescription>
              Archived templates do not appear in the tenant clone picker.
              Existing tenant clones live on. Use Restore to reverse.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onArchiveConfirm}>
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-3 py-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-right max-w-[60%] break-words">{children}</span>
    </div>
  );
}
